import { Link, useMatchRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Circle, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '../../ui/sidebar';
import { cn } from '../../ui/utils';
import { activeEntry, matchesRoute } from './topbar';
import { sh } from './messages';

/** A trailing affordance on the entry's right edge: an expand/collapse cue or a count. */
export type ShellNavIndicator =
  { kind: 'chevron' } | { kind: 'chevron-down' } | { kind: 'badge'; label: string };

export interface ShellNavItem {
  path: string;
  params?: Record<string, string>;
  /** A search value this exact entry needs the URL to carry, e.g. two
   *  children of one screen reached by `?view=`. Absent for an entry the URL's
   *  search never decides. See `topbar.ts`'s `activeEntry` for how a tie on
   *  `path` between two such children resolves. */
  search?: Record<string, string>;
  /** A second route this entry is current on (`topbar.ts`'s `TopbarEntry`). */
  activeOn?: string;
  label: string;
  icon?: LucideIcon;
  disabled?: boolean;
  indicator?: ShellNavIndicator;
  /** A second-level entry's trailing count; renders nothing when there is none. */
  badge?: ReactNode;
  /** A second level, rendered under this entry and collapsed or expanded by
   *  the reader; the rail-collapsed sidebar hides it by width alone
   *  (`ui/sidebar.tsx`), so there it opens as `RailFlyout`'s menu instead.
   *  A child sharing this entry's
   *  own `path` is its landing screen, and claims the active state ahead of
   *  the entry itself -- see `ShellNav`'s `shadowed` check. */
  children?: ShellNavItem[];
}

export interface ShellNavGroup {
  label: string | null;
  items: ShellNavItem[];
}

/**
 * Figma frame 116:78's Active state is a raised card (`bg-card` + edge +
 * `shadow-1`), not `ui/sidebar.tsx`'s own accent-fill default -- overridden
 * here at the call site rather than in that shared primitive, which every
 * other sidebar consumer still relies on for its own look. The edge is an
 * inset ring, not a border: a border takes 2px from the rail's 32px button
 * and pushes its icon off the rail's centre line.
 */
const activeItemClassName =
  'data-[active=true]:inset-ring data-[active=true]:inset-ring-sidebar-border data-[active=true]:bg-card data-[active=true]:font-normal data-[active=true]:text-foreground data-[active=true]:shadow-1 data-[active=true]:hover:bg-card data-[active=true]:hover:text-foreground';

/** Figma's trailing count is a `bg-secondary` chip, not the bare bold number `ui/sidebar.tsx`'s `SidebarMenuBadge` renders by default. */
const badgeClassName =
  'h-auto min-w-0 rounded-sm bg-secondary px-sm py-2xs font-normal text-caption text-muted-foreground';

/**
 * `SidebarMenuBadge` is `absolute right-1` over the button, outside the flex
 * row the label truncates against, so it visually covers the label's tail
 * instead of the label stopping short of it (rendered: "Painel" + a "12"
 * chip drawn on top of "ainel"). Reserving this much right padding gives the
 * label's own `truncate` a shorter box to fit in, ending before the badge.
 */
const badgeClearanceClassName = 'pr-10';

/**
 * Whether `entry` reads as the active row for `current`, the entry
 * `activeEntry` (`topbar.ts`) already picked as the best match. An entry with
 * no `search` of its own is active whenever its path is -- the original
 * one-path-one-screen case, where a shadowed parent and its overview child
 * share a path and both should read as "here". An entry that names a
 * `search` shares its path with a sibling that differs only there (a
 * `view=feed`/`view=list` pair), so it must be the exact entry that won.
 */
function isCurrent(entry: ShellNavItem, current: ShellNavItem | null): boolean {
  if (current === null || entry.path !== current.path) return false;
  return entry.search === undefined || entry === current;
}

/**
 * A group on the icon rail. `ui/sidebar.tsx` hides `SidebarMenuSub` there by
 * width alone, which would leave every second-level screen unreachable from
 * the nav between 1024 and 1439px (`Shell.tsx`'s forced rail), so the group's
 * icon opens its entries as a menu beside the rail instead. A parent that is
 * a screen of its own (not shadowed by a child on its path) leads the list.
 */
function RailFlyout(props: {
  item: ShellNavItem;
  entries: ShellNavItem[];
  holdsCurrent: boolean;
  current: ShellNavItem | null;
}): ReactNode {
  const Icon = props.item.icon ?? Circle;
  return (
    <SidebarMenuItem>
      <DropdownMenu>
        {/* No `tooltip`: `SidebarMenuButton` wraps a tooltip in its own root,
            which a trigger's `asChild` cannot attach to; the menu's own label
            names the group instead. */}
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton isActive={props.holdsCurrent} className={activeItemClassName}>
            <Icon aria-hidden />
            <span className="truncate">{props.item.label}</span>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start">
          <DropdownMenuLabel>{props.item.label}</DropdownMenuLabel>
          {props.entries.map((entry) => {
            const EntryIcon = entry.icon ?? Circle;
            const active = isCurrent(entry, props.current);
            return (
              <DropdownMenuItem
                key={`${entry.path}:${entry.label}`}
                asChild
                className={cn(active && 'bg-accent font-medium text-accent-foreground')}
              >
                <Link
                  to={entry.path}
                  params={entry.params as never}
                  search={entry.search as never}
                  activeOptions={{ exact: true }}
                  aria-current={active ? 'page' : undefined}
                >
                  <EntryIcon aria-hidden />
                  <span className="truncate">{entry.label}</span>
                  {entry.badge}
                </Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

function Item(props: {
  item: ShellNavItem;
  active: boolean;
  current: ShellNavItem | null;
  shadowed: boolean;
}): ReactNode {
  const Icon = props.item.icon ?? Circle;
  const indicator = props.item.indicator;
  const clearsBadge = indicator?.kind === 'badge';
  const enabledClassName = clearsBadge
    ? `${activeItemClassName} ${badgeClearanceClassName}`
    : activeItemClassName;
  // The same condition `ui/sidebar.tsx` hides `SidebarMenuSub` under.
  const { state, isMobile } = useSidebar();
  const railed = state === 'collapsed' && !isMobile;
  const children = props.item.children ?? [];
  // Open on first render and whenever the route moves into the group, so the
  // current entry is never hidden by arriving at it; the reader's toggle holds
  // otherwise, the current route's own group included.
  const holdsCurrent = props.active || children.some((child) => isCurrent(child, props.current));
  const [open, setOpen] = useState(true);
  const [heldCurrent, setHeldCurrent] = useState(holdsCurrent);
  if (holdsCurrent !== heldCurrent) {
    setHeldCurrent(holdsCurrent);
    if (holdsCurrent) setOpen(true);
  }
  if (railed && children.length > 0 && !props.item.disabled) {
    return (
      <RailFlyout
        item={props.item}
        entries={props.shadowed ? children : [props.item, ...children]}
        holdsCurrent={holdsCurrent}
        current={props.current}
      />
    );
  }
  const chevron = (
    <ChevronDown
      aria-hidden
      className={cn('ml-auto size-4 shrink-0 transition-transform', !open && '-rotate-90')}
    />
  );
  const content = (
    <>
      <Icon aria-hidden />
      {/* Named directly rather than relying on the button's `:last-child`
          truncation rule, which a trailing chevron or badge would otherwise
          claim instead of the label. */}
      <span className="truncate">{props.item.label}</span>
      {indicator?.kind === 'chevron' && (
        <ChevronRight aria-hidden className="ml-auto size-4 shrink-0" />
      )}
      {/* Only the heading form carries the chevron inline: a linked parent
          carries it as a separate toggle beside the link (below). */}
      {indicator?.kind === 'chevron-down' && props.shadowed && chevron}
    </>
  );
  return (
    <SidebarMenuItem>
      <Collapsible open={open} onOpenChange={setOpen}>
        {props.item.disabled ? (
          <SidebarMenuButton
            disabled
            tooltip={props.item.label}
            className={clearsBadge ? badgeClearanceClassName : undefined}
          >
            {content}
          </SidebarMenuButton>
        ) : props.shadowed ? (
          // A child owns this exact path (its landing screen), so this row is
          // the group's toggle, not a second link to the same place: a `Link`
          // here would still pick up TanStack's own exact-match `aria-current`,
          // stamping two "current" entries on one route.
          <CollapsibleTrigger asChild>
            <SidebarMenuButton tooltip={props.item.label} className={enabledClassName}>
              {content}
            </SidebarMenuButton>
          </CollapsibleTrigger>
        ) : (
          <SidebarMenuButton
            asChild
            isActive={props.active}
            tooltip={props.item.label}
            className={enabledClassName}
          >
            <Link
              to={props.item.path}
              params={props.item.params as never}
              // `Link` decides its own `aria-current` from a prefix match, so an
              // entry at a plane's root marks itself current on every screen
              // under it. Pinned to an exact match so the only non-exact case
              // left -- a child route with no entry of its own -- is the one
              // `activePath` grants deliberately, on the line below.
              activeOptions={{ exact: true }}
              aria-current={props.active ? 'page' : undefined}
            >
              {content}
            </Link>
          </SidebarMenuButton>
        )}
        {indicator?.kind === 'badge' && (
          <SidebarMenuBadge className={badgeClassName}>{indicator.label}</SidebarMenuBadge>
        )}
        {children.length > 0 && !props.shadowed && !props.item.disabled && (
          <CollapsibleTrigger asChild>
            <SidebarMenuAction aria-label={sh('nav.group.toggle', { label: props.item.label })}>
              {chevron}
            </SidebarMenuAction>
          </CollapsibleTrigger>
        )}
        {children.length > 0 && (
          <CollapsibleContent asChild>
            <SidebarMenuSub>
              {children.map((child) => {
                const ChildIcon = child.icon ?? Circle;
                const childActive = isCurrent(child, props.current);
                return (
                  <SidebarMenuSubItem key={`${child.path}:${child.label}`}>
                    <SidebarMenuSubButton asChild isActive={childActive}>
                      <Link
                        to={child.path}
                        params={child.params as never}
                        search={child.search as never}
                        activeOptions={{ exact: true }}
                        aria-current={childActive ? 'page' : undefined}
                      >
                        <ChildIcon aria-hidden />
                        <span className="truncate">{child.label}</span>
                        {child.badge}
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
          </CollapsibleContent>
        )}
      </Collapsible>
    </SidebarMenuItem>
  );
}

/** `<nav aria-label={label}>` of SidebarGroups; returns null when every group is empty. */
export function ShellNav(props: { label: string; groups: ShellNavGroup[] }): ReactNode {
  const matchRoute = useMatchRoute();
  const groups = props.groups.filter((group) => group.items.length > 0);
  // Across every group, not within one: the groups are a visual arrangement,
  // and an entry in one is as much an ancestor of an entry in another. A
  // child is matched on its own path alongside its parent's, so a route only
  // a child owns can still become current; see the `shadowed` check below
  // for the reverse case, where the two share a path.
  const current = activeEntry(
    (item) => matchesRoute(matchRoute, item),
    groups.flatMap((group) =>
      group.items.flatMap((item) => (item.children ? [item, ...item.children] : [item])),
    ),
  );
  if (groups.length === 0) return null;
  return (
    <nav aria-label={props.label}>
      {groups.map((group, index) => (
        <SidebarGroup key={group.label ?? index}>
          {group.label !== null && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {group.items.map((item) => {
                // A child registered at its parent's own path (the group's
                // landing screen) is the one that should read as current;
                // `Item` itself withholds this from the shadowed heading.
                const shadowed = item.children?.some((child) => child.path === item.path) ?? false;
                return (
                  <Item
                    key={item.path}
                    item={item}
                    active={isCurrent(item, current)}
                    current={current}
                    shadowed={shadowed}
                  />
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </nav>
  );
}
